import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import { AnimatedSection, SectionBadge } from './shared';
import { PARCOURS } from '../data/parcoursData';

/**
 * ObjectivesSection — "Choisis ton parcours" sur la home page.
 * Affiche les 5 parcours définis dans parcoursData.js sous forme de cartes interactives.
 * Chaque carte mène à /parcours/{slug} où l'utilisateur trouve le déroulé complet.
 */
const ObjectivesSection = ({ language = 'fr' }) => {
  const en = language === 'en';
  const L = (fr, eng) => (en ? eng : fr);

  return (
    <section className="py-20 bg-gradient-to-b from-black via-zinc-950 to-black relative overflow-hidden" data-testid="objectives-section">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-3xl opacity-50" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <AnimatedSection>
          <div className="text-center mb-14">
            <SectionBadge emoji="🎯">{L('VOTRE OBJECTIF', 'YOUR GOAL')}</SectionBadge>
            <h2 className="text-3xl md:text-5xl font-bold text-white mt-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {L('Choisissez ', 'Choose ')}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-pink-500 to-yellow-400">
                {L('votre parcours', 'your path')}
              </span>
            </h2>
            <p className="text-gray-400 mt-4 max-w-2xl mx-auto text-lg">
              {L(
                "5 accompagnements pensés pour des objectifs précis. Clique sur celui qui te ressemble.",
                "5 dedicated programs for specific goals. Click the one that fits you best."
              )}
            </p>
          </div>
        </AnimatedSection>

        {/* Grille 5 cartes — responsive : 1 col mobile, 2 col tablet, 5 col desktop */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
          {PARCOURS.map((p, i) => {
            const Icon = p.icon;
            return (
              <AnimatedSection key={p.slug} delay={i * 80}>
                <Link
                  to={`/parcours/${p.slug}`}
                  className="group relative block h-full rounded-3xl border border-white/10 bg-gradient-to-br from-zinc-900 to-black p-6 hover:-translate-y-2 hover:border-white/30 transition-all duration-500 overflow-hidden"
                  data-testid={`parcours-card-${p.slug}`}
                >
                  {p.badge && (
                    <span className="absolute top-3 right-3 px-2 py-0.5 rounded-full text-[9px] font-black tracking-wider text-black bg-gradient-to-r from-yellow-400 to-amber-500 shadow-lg flex items-center gap-1">
                      <Sparkles className="w-2.5 h-2.5" />
                      {p.badge}
                    </span>
                  )}

                  {/* Glow corner on hover */}
                  <div
                    className="absolute -top-16 -right-16 w-40 h-40 rounded-full blur-3xl opacity-0 group-hover:opacity-60 transition-opacity duration-500"
                    style={{ background: p.glowRgba }}
                    aria-hidden="true"
                  />

                  <div className={`relative inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br ${p.gradient} shadow-lg mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className="w-7 h-7 text-white" strokeWidth={2} />
                  </div>

                  <h3 className="relative text-lg font-bold text-white mb-1.5 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {p.label}
                  </h3>
                  <p className="relative text-zinc-400 text-xs mb-4 leading-relaxed line-clamp-2">
                    {p.tagline}
                  </p>

                  <span className={`relative inline-flex items-center gap-1.5 ${p.accentClass} font-bold text-xs group-hover:gap-2.5 transition-all`}>
                    Voir le parcours
                    <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </AnimatedSection>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ObjectivesSection;
